import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { SelectInput, TextInput } from "@/components/ui/Field";
import { LieferantSearchField } from "@/components/LieferantSearchField";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  commitArtikelImport,
  deleteArtikelImportJob,
  getArtikelImportJob,
  IMPORT_AKTION_LABELS,
  IMPORT_FELD_LABELS,
  IMPORT_MATCH_FELDER,
  IMPORT_ZIELFELDER,
  setArtikelImportMapping,
  uploadArtikelImport,
  type ArtikelImportAktion,
  type ArtikelImportJob,
  type ArtikelImportZeile,
  type ImportMatchFeld,
} from "@/lib/artikelImport";

type Step = "upload" | "mapping" | "vorschau" | "fertig";

const PREVIEW_PAGE_SIZE = 50;

const AKTION_BADGES: Record<ArtikelImportAktion, string> = {
  neu: "bg-status-green/15 text-status-green",
  aktualisieren: "bg-accent-soft text-accent",
  unveraendert: "bg-fill-tertiary text-label-secondary",
  uebersprungen: "bg-status-orange/15 text-status-orange",
  konflikt: "bg-status-red/15 text-status-red",
};

export interface ArtikelImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Wird nach erfolgreichem Import aufgerufen (Liste neu laden). */
  onImported: () => void;
}

export function ArtikelImportDialog({ open, onClose, onImported }: ArtikelImportDialogProps) {
  const [step, setStep] = useState<Step>("upload");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Upload
  const [files, setFiles] = useState<File[]>([]);
  const [lieferant, setLieferant] = useState("");
  /** Job-weites Gültig-bis für alle importierten Preise (YYYY-MM-DD). */
  const [gueltigBis, setGueltigBis] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Job / Excel-Mapping
  const [job, setJob] = useState<ArtikelImportJob | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [beispiele, setBeispiele] = useState<(string | number | null)[][]>([]);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [matchFeld, setMatchFeld] = useState<ImportMatchFeld>("lieferanten_artnr");

  // Vorschau
  const [rows, setRows] = useState<ArtikelImportZeile[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [aktionFilter, setAktionFilter] = useState("");
  const [previewOffset, setPreviewOffset] = useState(0);

  const isExcel = useMemo(
    () => files.some((f) => /\.(xlsx|xlsm|xls)$/i.test(f.name)),
    [files],
  );

  const reset = useCallback(() => {
    setStep("upload");
    setBusy(false);
    setError(null);
    setFiles([]);
    setLieferant("");
    setGueltigBis("");
    setJob(null);
    setHeaders([]);
    setBeispiele([]);
    setMapping({});
    setMatchFeld("lieferanten_artnr");
    setRows([]);
    setRowsTotal(0);
    setAktionFilter("");
    setPreviewOffset(0);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  function handleClose() {
    // Nicht abgeschlossene Jobs serverseitig aufräumen (best effort)
    if (job && step !== "fertig") {
      void deleteArtikelImportJob(job.id).catch(() => undefined);
    }
    onClose();
  }

  function errMessage(err: unknown): string {
    return err instanceof ApiError ? err.message : "Unerwarteter Fehler beim Import.";
  }

  async function handleUpload() {
    if (files.length === 0) {
      setError("Bitte zuerst eine Datei auswählen.");
      return;
    }
    const quelle = isExcel ? "excel" : "datanorm";
    if (quelle === "datanorm" && !lieferant.trim()) {
      setError("Für Datanorm-Importe bitte den Lieferanten angeben.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await uploadArtikelImport(
        files,
        quelle,
        lieferant.trim(),
        gueltigBis.trim() || undefined,
      );
      setJob(result.job);
      if (result.job.quelle === "excel") {
        setHeaders(result.headers ?? []);
        setBeispiele(result.beispiele ?? []);
        setStep("mapping");
      } else {
        setStep("vorschau");
        await loadPreview(result.job.id, "", 0);
      }
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleMapping() {
    if (!job) return;
    const mapped = Object.values(mapping).filter(Boolean);
    if (matchFeld !== "ean" && !mapped.includes("artikelnummer")) {
      setError("Bitte eine Spalte als Artikelnummer zuordnen.");
      return;
    }
    if (matchFeld === "ean" && !mapped.includes("ean")) {
      setError("Bitte eine Spalte als EAN zuordnen.");
      return;
    }
    if (matchFeld === "lieferanten_artnr" && !lieferant.trim()) {
      setError("Bitte den Lieferanten angeben (für den Abgleich über die Lieferanten-Artikelnummer).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string> = {};
      for (const [idx, feld] of Object.entries(mapping)) {
        if (feld) body[idx] = feld;
      }
      const result = await setArtikelImportMapping(job.id, {
        mapping: body,
        matchFeld,
        lieferant: lieferant.trim() || undefined,
      });
      setJob(result.job);
      setStep("vorschau");
      await loadPreview(job.id, "", 0);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const loadPreview = useCallback(
    async (jobId: number, aktion: string, offset: number) => {
      const result = await getArtikelImportJob(jobId, {
        aktion: aktion || undefined,
        limit: PREVIEW_PAGE_SIZE,
        offset,
      });
      setJob(result.job);
      setRows(result.rows);
      setRowsTotal(result.total);
      setAktionFilter(aktion);
      setPreviewOffset(offset);
    },
    [],
  );

  async function handleFilterChange(aktion: string) {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      await loadPreview(job.id, aktion, 0);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handlePreviewPage(offset: number) {
    if (!job) return;
    setBusy(true);
    try {
      await loadPreview(job.id, aktionFilter, offset);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const result = await commitArtikelImport(job.id);
      setJob(result.job);
      setStep("fertig");
      onImported();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const summary = job?.zusammenfassung ?? null;
  const anwendbar = (summary?.neu ?? 0) + (summary?.aktualisieren ?? 0);
  const importWarnungen = (() => {
    const meta = (job?.konfiguration as { meta?: { warnungen?: unknown } } | null)?.meta;
    const list = meta?.warnungen;
    return Array.isArray(list) ? list.filter((w): w is string => typeof w === "string") : [];
  })();

  const title =
    step === "upload"
      ? "Artikel importieren"
      : step === "mapping"
        ? "Excel-Spalten zuordnen"
        : step === "vorschau"
          ? "Import-Vorschau"
          : "Import abgeschlossen";

  const footer = (() => {
    if (step === "upload") {
      return (
        <>
          <DialogCancelButton onClose={handleClose} disabled={busy} />
          <Button onClick={() => void handleUpload()} loading={busy} disabled={files.length === 0}>
            {isExcel ? "Weiter zur Zuordnung" : "Datei prüfen"}
          </Button>
        </>
      );
    }
    if (step === "mapping") {
      return (
        <>
          <DialogCancelButton onClose={handleClose} disabled={busy} />
          <Button onClick={() => void handleMapping()} loading={busy}>
            Vorschau erstellen
          </Button>
        </>
      );
    }
    if (step === "vorschau") {
      return (
        <>
          <DialogCancelButton onClose={handleClose} disabled={busy} />
          <Button onClick={() => void handleCommit()} loading={busy} disabled={anwendbar === 0}>
            {anwendbar === 0
              ? "Keine Änderungen anwendbar"
              : `${anwendbar.toLocaleString("de-DE")} Änderung${anwendbar === 1 ? "" : "en"} übernehmen`}
          </Button>
        </>
      );
    }
    return (
      <Button onClick={onClose}>Schließen</Button>
    );
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      dirty={step !== "fertig" && (files.length > 0 || job != null)}
      title={title}
      size={step === "upload" ? "lg" : "3xl"}
      footer={footer}
      bodyClassName="overflow-y-auto max-h-[70vh]"
    >
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {step === "upload" && (
          <>
            <p className="text-sm text-label-secondary">
              Unterstützt werden Datanorm-4-Kataloge (auch mehrere Dateien, z.&nbsp;B.{" "}
              <span className="font-mono">DATANORM.001</span>,{" "}
              <span className="font-mono">DATPREIS.001</span>,{" "}
              <span className="font-mono">.WRG</span>, <span className="font-mono">.RAB</span>)
              sowie Excel-Listen (.xlsx). Vor dem Import wird immer eine Vorschau angezeigt – es
              wird nichts ohne Bestätigung geändert.
            </p>
            <div
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-ios border-2 border-dashed border-separator px-4 py-8 text-center transition hover:border-accent/60 hover:bg-fill-tertiary/40"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".001,.002,.003,.004,.005,.rab,.wrg,.RAB,.WRG,.txt,.dat,.datanorm,.xlsx,.xlsm,.xls"
                className="hidden"
                onChange={(e) => {
                  setFiles(Array.from(e.target.files ?? []));
                  setError(null);
                }}
              />
              {files.length === 0 ? (
                <>
                  <span className="text-sm font-medium text-label">Datei auswählen</span>
                  <span className="text-xs text-label-tertiary">
                    Datanorm (.001/.WRG/.RAB, DATPREIS) oder Excel (.xlsx)
                  </span>
                </>
              ) : (
                <div className="space-y-1">
                  {files.map((f) => (
                    <div key={f.name} className="font-mono text-sm text-label">
                      {f.name}{" "}
                      <span className="text-label-tertiary">
                        ({(f.size / 1024).toLocaleString("de-DE", { maximumFractionDigits: 0 })} KB)
                      </span>
                    </div>
                  ))}
                  <div className="text-xs text-accent">Klicken zum Ändern</div>
                </div>
              )}
            </div>
            <LieferantSearchField
              label={isExcel ? "Lieferant (optional, für den Abgleich)" : "Lieferant"}
              value={lieferant}
              onChange={({ name }) => setLieferant(name)}
              placeholder="Lieferant aus Stammdaten suchen …"
              required={!isExcel}
              hint="Artikelnummern werden pro Lieferant gespeichert, damit erneute Importe keine Duplikate anlegen."
            />
            <TextInput
              label="Gültig bis (für importierte Preise)"
              type="date"
              value={gueltigBis}
              onChange={(e) => setGueltigBis(e.target.value)}
              hint="Optional. Nach diesem Datum fließt der Preis nicht mehr in Dokumente ein (0 €), bleibt aber als Orientierungspreis sichtbar. In Excel kann eine Spalte „Gültig bis“ das Datum pro Zeile überschreiben."
            />
          </>
        )}

        {step === "mapping" && (
          <>
            <p className="text-sm text-label-secondary">
              Bitte den Spalten der Excel-Datei die passenden Artikel-Felder zuordnen. Nicht
              zugeordnete Spalten werden ignoriert.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectInput
                label="Abgleich über"
                value={matchFeld}
                onChange={(e) => setMatchFeld(e.target.value as ImportMatchFeld)}
                hint="Bestimmt, wie vorhandene Artikel erkannt werden (Duplikat-Schutz)."
              >
                {IMPORT_MATCH_FELDER.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </SelectInput>
              {matchFeld === "lieferanten_artnr" && (
                <LieferantSearchField
                  label="Lieferant"
                  value={lieferant}
                  onChange={({ name }) => setLieferant(name)}
                  required
                />
              )}
            </div>
            <div className="overflow-hidden rounded-ios border border-separator">
              <table className="w-full text-sm">
                <thead className="bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Spalte in der Datei</th>
                    <th className="px-3 py-2 text-left font-semibold">Beispielwerte</th>
                    <th className="w-56 px-3 py-2 text-left font-semibold">Zuordnung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-separator bg-card">
                  {headers.map((header, idx) => {
                    const samples = beispiele
                      .map((row) => row[idx])
                      .filter((v) => v != null && String(v).trim() !== "")
                      .slice(0, 3);
                    return (
                      <tr key={`${header}-${idx}`}>
                        <td className="px-3 py-2 font-medium text-label">{header}</td>
                        <td className="max-w-[16rem] truncate px-3 py-2 text-label-tertiary">
                          {samples.length > 0 ? samples.map(String).join(" · ") : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <SelectInput
                            value={mapping[idx] ?? ""}
                            selectSize="sm"
                            onChange={(e) =>
                              setMapping((prev) => ({ ...prev, [idx]: e.target.value }))
                            }
                          >
                            <option value="">Ignorieren</option>
                            {IMPORT_ZIELFELDER.map((f) => (
                              <option
                                key={f.value}
                                value={f.value}
                                disabled={
                                  f.value !== (mapping[idx] ?? "") &&
                                  Object.values(mapping).includes(f.value)
                                }
                              >
                                {f.label}
                              </option>
                            ))}
                          </SelectInput>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {step === "vorschau" && (
          <>
            {importWarnungen.length > 0 ? (
              <Alert variant="warning">
                <ul className="list-disc space-y-1 pl-4">
                  {importWarnungen.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
            {summary && (
              <div className="flex flex-wrap gap-2">
                {(
                  ["neu", "aktualisieren", "unveraendert", "uebersprungen", "konflikt"] as ArtikelImportAktion[]
                ).map((aktion) => {
                  const count = summary[aktion] ?? 0;
                  const active = aktionFilter === aktion;
                  return (
                    <button
                      key={aktion}
                      type="button"
                      onClick={() => void handleFilterChange(active ? "" : aktion)}
                      className={clsx(
                        "rounded-full px-3 py-1 text-sm font-medium transition",
                        AKTION_BADGES[aktion],
                        active && "ring-2 ring-accent",
                        count === 0 && "opacity-50",
                      )}
                    >
                      {IMPORT_AKTION_LABELS[aktion]}: {count.toLocaleString("de-DE")}
                    </button>
                  );
                })}
              </div>
            )}

            {busy && rows.length === 0 ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : (
              <div className="overflow-hidden rounded-ios border border-separator">
                <table className="w-full text-sm">
                  <thead className="bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Artikelnr.</th>
                      <th className="px-3 py-2 text-left font-semibold">Name</th>
                      <th className="px-3 py-2 text-left font-semibold">Aktion</th>
                      <th className="px-3 py-2 text-left font-semibold">Änderungen / Hinweis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-separator bg-card">
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-label-tertiary">
                          Keine Zeilen für diesen Filter.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => (
                      <tr key={row.id} className="align-top">
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-label">
                          {String(row.daten?.artikelnummer ?? "—")}
                        </td>
                        <td className="max-w-[18rem] px-3 py-2 text-label">
                          <span className="line-clamp-2">{String(row.daten?.name ?? "—")}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {row.aktion ? (
                            <span
                              className={clsx(
                                "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
                                AKTION_BADGES[row.aktion],
                              )}
                            >
                              {IMPORT_AKTION_LABELS[row.aktion]}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-label-secondary">
                          {row.hinweis && <div className="text-status-orange">{row.hinweis}</div>}
                          {(row.aenderungen ?? []).slice(0, 6).map((a, i) => (
                            <div key={i}>
                              <span className="font-medium">
                                {IMPORT_FELD_LABELS[a.feld] ?? a.feld}
                              </span>
                              {": "}
                              {a.alt != null && a.alt !== "" ? (
                                <>
                                  <span className="line-through opacity-70">{String(a.alt)}</span>{" "}
                                  →{" "}
                                </>
                              ) : null}
                              <span className="text-label">{String(a.neu ?? "—")}</span>
                            </div>
                          ))}
                          {(row.aenderungen?.length ?? 0) > 6 && (
                            <div className="text-label-tertiary">
                              +{(row.aenderungen?.length ?? 0) - 6} weitere Änderungen
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {rowsTotal > PREVIEW_PAGE_SIZE && (
              <div className="flex items-center justify-between text-sm text-label-secondary">
                <span>
                  Zeige {(previewOffset + 1).toLocaleString("de-DE")}–
                  {Math.min(previewOffset + PREVIEW_PAGE_SIZE, rowsTotal).toLocaleString("de-DE")} von{" "}
                  {rowsTotal.toLocaleString("de-DE")}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={previewOffset <= 0 || busy}
                    onClick={() => void handlePreviewPage(Math.max(0, previewOffset - PREVIEW_PAGE_SIZE))}
                  >
                    Zurück
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={previewOffset + PREVIEW_PAGE_SIZE >= rowsTotal || busy}
                    onClick={() => void handlePreviewPage(previewOffset + PREVIEW_PAGE_SIZE)}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {step === "fertig" && summary && (
          <Alert variant="success" title="Import erfolgreich abgeschlossen">
            <ul className="mt-1 space-y-0.5">
              <li>{(summary.angewendet_neu ?? 0).toLocaleString("de-DE")} Artikel neu angelegt</li>
              <li>
                {(summary.angewendet_aktualisiert ?? 0).toLocaleString("de-DE")} Artikel aktualisiert
              </li>
              {(summary.angewendet_uebersprungen ?? 0) > 0 && (
                <li>
                  {(summary.angewendet_uebersprungen ?? 0).toLocaleString("de-DE")} Zeilen übersprungen
                  (geschützt oder nicht zuordenbar)
                </li>
              )}
            </ul>
            <p className="mt-2 text-xs text-label-secondary">
              Alle Änderungen sind in der Artikel-Historie dokumentiert.
            </p>
          </Alert>
        )}
      </div>
    </Dialog>
  );
}
